<script>
import VDataTableCustom from "@/components/VDataTable/VDataTableCustom.vue";
import TextEllipsis from "@/components/commonComponents/TextEllipsis.vue";
import NumberFormat from "@/mixins/NumberFormat";
import VIconTooltip from "@/components/commonComponents/VIconTooltip.vue";
import Clipboard from '@/mixins/Clipboard';

export default {
    name: "VDataTableHistory",
    components: {
        VIconTooltip,
        TextEllipsis,
        VDataTableCustom,
    },
    inheritAttrs: false,
    mixins: [
        NumberFormat,
        Clipboard,
    ],
    props: {
        footerProps: {
            type: Object,
            default: () => ({itemsPerPageOptions:[10]})
        },
        groupBy: {
            type: String,
            default: "source_name"
        }
    },
    data() {
        return {
            headers: [
                {
                    text: 'ID',
                    align: 'start',
                    sortable: true,
                    value: 'id',
                    width: 100,
                    filterComponent: 'VDataTableCustomHeaderFiltersText',
                    tooltip: 'Идентификатор в системе учета ESS',
                    groupable: false
                },
                {
                    text: '&#9776;',
                    align: 'start',
                    sortable: true,
                    value: 'action',
                    width: 50,
                    filterComponent: 'VDataTableCustomHeaderFiltersAutocomplete',
                    filterComponentProps: {
                        mapValuesFn: (v) => {
                            return {I: "Добавление", U: "Обновление", D: "Удаление"}[v] || v;
                        },
                        itemTextWithHtml: true,
                    },
                    filterLabel: 'Тип операции',
                    tooltip: 'Тип операции',
                    groupable: false
                },
                {
                    text: 'Источник',
                    align: 'start',
                    sortable: true,
                    value: 'source_name',
                    width: 100,
                    filterComponent: 'VDataTableCustomHeaderFiltersAutocomplete',
                },
                /**
                 * Конечному пользователю он, наверное, ни к чему. Скорее для нас, в целях группировки, при необходимости...
                 */
                /*                {
                    text: 'Номер пакета',
                    align: 'start',
                    sortable: true,
                    value: 'batch',
                    width: 100,
                    filterComponent: 'VDataTableCustomHeaderFiltersText',
                },*/
                {
                    text: 'Поле',
                    align: 'start',
                    sortable: true,
                    value: 'field_description',
                    width: 100,
                    filterComponent: 'VDataTableCustomHeaderFiltersText',
                },
                {
                    text: 'До',
                    align: 'start',
                    sortable: true,
                    value: 'old',
                    width: 100,
                    // filterComponent: 'VDataTableCustomHeaderFiltersText',
                    groupable: false
                },
                {
                    text: 'После',
                    align: 'start',
                    sortable: true,
                    value: 'new',
                    width: 100,
                    // filterComponent: 'VDataTableCustomHeaderFiltersText',
                    groupable: false
                },
                {
                    text: 'Комментарий',
                    align: 'start',
                    sortable: true,
                    value: 'description',
                    width: 100,
                    // filterComponent: 'VDataTableCustomHeaderFiltersText',
                    groupable: false
                },
                {
                    text: 'Дата',
                    align: 'center',
                    sortable: true,
                    value: 'created_at',
                    filterComponent: 'VDataTableCustomHeaderFiltersDate',
                    width: 120,
                    groupable: false
                },
                {
                    text: 'Автор',
                    align: 'start',
                    sortable: true,
                    value: 'created_by_name',
                    width: 100,
                    filterComponent: 'VDataTableCustomHeaderFiltersAutocomplete',
                },
            ],
        };
    },
    methods: {
        getUserName(item) {
            return (item.created_by_first_name || item.created_by_last_name) ?
                [item.created_by_first_name, item.created_by_last_name].join(" ") :
                item.created_by_email;
        },
        prettyPrint(value, type) {
            if (['bit'].includes(type)) {
                return this.prettyBool(value);
            } else if (['tinyint', 'smallint', 'mediumint', 'int', 'bigint'].includes(type)) {
                return this.prettyInt(value);
            } else if (['float', 'double', 'decimal', 'numeric'].includes(type)) {
                return this.prettyFloat(value);
            } else if (['datetime', 'timestamp'].includes(type)) {
                return this.$options.filters.moment(value, 'DD.MM.Y HH:mm');
            } else if (['date'].includes(type)) {
                return this.$options.filters.moment(value, 'DD.MM.Y');
            } else {
                return value;
            }
        },
    },
}
</script>

<template>
    <v-data-table-custom
        v-bind="$attrs"
        v-on="$listeners"
        :headers="headers"
        heading="История"
        sort-by="created_at"
        sort-desc
        item-key="id"
        with-toolbar
        filter
        search=""
        :group-by="groupBy"
        show-group-by
        :footer-props="footerProps"
    >
        <!-- @TODO: при необходимости есть возможность разворачивать подробную информацию о строке https://v2.vuetifyjs.com/en/components/data-tables/#expandable-rows -->
        <template v-slot:item.field_description="{value, item}">
            {{ value || item.field_name }}
        </template>
        <template v-for="(column) in ['old', 'new', 'description']" v-slot:[`item.${column}`]="{ value, item }">
            <v-hover v-slot="{hover}" :key="`${column}-${item.id}`">
                <div class="container-actions">
                    <div class="actions" v-if="hover">
                        <v-icon-tooltip
                            small
                            icon="mdi-clipboard-text-multiple-outline"
                            @click="copyToClipboard(prettyPrint(value, item.field_data_type))"
                        >
                            Скопировать в буфер обмена
                        </v-icon-tooltip>
                    </div>
                    <text-ellipsis :multiline="2" style="max-width: 100px">
                        {{ column !== "description" ? prettyPrint(value, item.field_data_type) : value }}
                    </text-ellipsis>
                </div>
            </v-hover>
        </template>
        <template v-slot:item.action="{value}">
            <v-icon-tooltip
                small
                v-if="value === 'I'"
                icon="mdi-file-document-plus-outline">
                Добавление
            </v-icon-tooltip>
            <v-icon-tooltip
                small
                v-if="value === 'U'"
                icon="mdi-file-document-edit-outline">
                Изменение
            </v-icon-tooltip>
            <v-icon-tooltip
                small
                v-if="value === 'D'"
                icon="mdi-file-document-remove-outline">
                Удаление
            </v-icon-tooltip>
        </template>
<!--        <template v-slot:group.header="{ group, headers, toggle, isOpen }">
            <td :colspan="headers.length">
                <v-btn @click="toggle" x-small icon :ref="group">
                    <v-icon v-if="isOpen">mdi-plus</v-icon>
                    <v-icon v-else>mdi-minus</v-icon>
                </v-btn>
                <span class="mx-5 font-weight-bold">{{ group }}</span>
            </td>
        </template>-->
        <!--        <template v-slot:item.created_by="{item}">
                    {{ getUserName(item) }}
                </template>-->
    </v-data-table-custom>
</template>

<style scoped lang="css">
.container-actions {
    position: relative;
}

.actions {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: -1em;
}

::v-deep .v-data-table-header th span:last-child:not(:first-child){
  font-size: 0; /* скрываем текст */
  position: relative;
  display: inline-block;
  top: -2px;
}

::v-deep .v-data-table-header th span:last-child:not(:first-child)::before {
  content: '\F1860'; /* mdi-format-list-group */
  font-family: 'Material Design Icons';
  font-size: 17px;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  color: inherit;
  font-weight: normal;
}
</style>
